import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Truck, Ban } from 'lucide-react';

const OwnerAlerts = () => {
    const [alerts, setAlerts] = useState([]);
    const [unfulfilled, setUnfulfilled] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [expandedUnfulfilled, setExpandedUnfulfilled] = useState(null);
    const popupShownRef = useRef(false);
    const [activeTab, setActiveTab] = useState('fulfillment');

    // Block Modal State
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockTarget, setBlockTarget] = useState(null);
    const [blockDuration, setBlockDuration] = useState('1');
    const [blocking, setBlocking] = useState(false);

    // Manual Assign State
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [orderToAssign, setOrderToAssign] = useState(null);
    const [entities, setEntities] = useState({ manufacturers: [], distributors: [] });
    const [selectedEntity, setSelectedEntity] = useState({ type: '', id: '' });
    const [assigning, setAssigning] = useState(false);

    const fetchAlerts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://rotica.in/get_alerts.php');

            if (response.data.success) {
                setAlerts(response.data.alerts || []);
                setUnfulfilled(response.data.unfulfilled_assignments || []);

                // Show popup notification if there are critical issues and popup hasn't been shown yet
                if (!popupShownRef.current) {
                    const criticalAlerts = (response.data.alerts || []).filter(
                        a => a.warning_count > 7 || a.alert_flag > 0
                    );
                    const unfulfilledCount = (response.data.unfulfilled_assignments || []).length;

                    if (criticalAlerts.length > 0 || unfulfilledCount > 0) {
                        popupShownRef.current = true; // Mark as shown immediately
                        let msg = "Important Attention Required:\n";
                        if (unfulfilledCount > 0) msg += `- ${unfulfilledCount} Orders could not be assigned to any candidate.\n`;
                        if (criticalAlerts.length > 0) msg += `- ${criticalAlerts.length} Entities have critical warning/alert levels.\n`;

                        alert(msg);
                    }
                }
            } else {
                setError(response.data.message || "Failed to fetch alerts.");
            }
        } catch (err) {
            console.error(err);
            setError("Network Error: Could not fetch alerts.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const options = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString('en-IN', options);
    };

    const fetchEntities = useCallback(async () => {
        try {
            const response = await axios.get('https://rotica.in/get_entities.php');
            if (response.data.success) {
                setEntities({
                    manufacturers: response.data.manufacturers,
                    distributors: response.data.distributors
                });
            }
        } catch (err) {
            console.error('Failed to fetch entities:', err);
        }
    }, []);

    const handleAssignClick = (order) => {
        setOrderToAssign(order);
        setSelectedEntity({ type: '', id: '' });
        setShowAssignModal(true);
        if (entities.manufacturers.length === 0 && entities.distributors.length === 0) {
            fetchEntities();
        }
    };

    const confirmManualAssign = async () => {
        if (!selectedEntity.type || !selectedEntity.id || !orderToAssign) {
            alert('Please select an entity to assign');
            return;
        }

        setAssigning(true);
        try {
            const formData = new FormData();
            formData.append('order_id', orderToAssign.order_id);
            formData.append('assigned_type', selectedEntity.type);
            formData.append('assigned_id', selectedEntity.id);

            const response = await axios.post('https://rotica.in/manual_assign_order.php', formData);

            if (response.data.success) {
                alert('Order assigned successfully');
                setShowAssignModal(false);
                fetchAlerts();
            } else {
                alert('Assignment failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error during manual assignment');
        } finally {
            setAssigning(false);
        }
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '-';
        const hour = parseInt(timeStr.split(':')[0]);
        if (hour === 7) return '7-10 AM';
        if (hour === 12) return '12-3 PM';
        if (hour === 18) return '6-9 PM';
        return timeStr;
    };

    // Handle block button click
    const handleBlockClick = (alert) => {
        setBlockTarget(alert);
        setBlockDuration('1');
        setShowBlockModal(true);
    };

    // Confirm block
    const confirmBlock = async () => {
        if (!blockTarget) return;

        setBlocking(true);
        try {
            const formData = new FormData();
            formData.append('entity_type', blockTarget.assigned_type);
            formData.append('entity_id', blockTarget.assigned_id);
            formData.append('duration', blockDuration);

            const response = await axios.post('https://rotica.in/block_entity.php', formData);

            if (response.data.success) {
                alert(`${blockTarget.assigned_type} #${blockTarget.assigned_id} has been blocked.`);
                fetchAlerts();
            } else {
                alert('Block failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error during blocking');
        } finally {
            setBlocking(false);
            setShowBlockModal(false);
            setBlockTarget(null);
        }
    };

    // Handle unblock
    const handleUnblock = async (alertItem) => {
        if (!window.confirm(`Unblock ${alertItem.assigned_type} "${alertItem.entity_name}"?`)) return;

        try {
            const formData = new FormData();
            formData.append('entity_type', alertItem.assigned_type);
            formData.append('entity_id', alertItem.assigned_id);

            const response = await axios.post('https://rotica.in/unblock_entity.php', formData);

            if (response.data.success) {
                alert(`${alertItem.assigned_type} has been unblocked.`);
                fetchAlerts();
            } else {
                alert('Unblock failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error during unblocking');
        }
    };

    // Handle ignore fulfillment alert
    const handleIgnore = async (alertItem) => {
        try {
            const formData = new FormData();
            formData.append('fulfillment_id', alertItem.id);

            const response = await axios.post('https://rotica.in/ignore_alert.php', formData);

            if (response.data.success) {
                setAlerts(prev => prev.filter(a => a.id !== alertItem.id));
            } else {
                alert('Ignore failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error');
        }
    };

    // Handle ignore assignment failure alert
    const handleIgnoreAssignment = async (orderId) => {
        try {
            const formData = new FormData();
            formData.append('order_id', orderId);

            const response = await axios.post('https://rotica.in/ignore_assignment_alert.php', formData);

            if (response.data.success) {
                setUnfulfilled(prev => prev.filter(a => a.order_id !== orderId));
            } else {
                alert('Ignore failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error');
        }
    };

    // Toggle row expansion
    const toggleRow = (id) => {
        setExpandedRow(expandedRow === id ? null : id);
    };

    const toggleUnfulfilledRow = (id) => {
        setExpandedUnfulfilled(expandedUnfulfilled === id ? null : id);
    };

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <header className="page-header">
                    <h1>Alerts & Notifications</h1>
                    <p className="subtitle">System monitoring and critical assignment failures</p>
                </header>

                <div className="stats-cards">
                    <div className="stat-card danger" onClick={() => setActiveTab('fulfillment')}>
                        <div className="stat-icon entity-icon"><Truck size={24} /></div>
                        <div className="stat-info">
                            <h3>Fulfillment Issues</h3>
                            <p>{alerts.length}</p>
                        </div>
                    </div>
                    <div className="stat-card warning" onClick={() => setActiveTab('assignment')}>
                        <div className="stat-icon assignment-icon"><Ban size={24} /></div>
                        <div className="stat-info">
                            <h3>Unassigned Orders</h3>
                            <p>{unfulfilled.length}</p>
                        </div>
                    </div>
                </div>

                <div className="alerts-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'fulfillment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fulfillment')}
                    >
                        Entity Fulfillment Alerts ({alerts.length})
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'assignment' ? 'active' : ''} ${unfulfilled.length > 0 ? 'highlight' : ''}`}
                        onClick={() => setActiveTab('assignment')}
                    >
                        Assignment Failures ({unfulfilled.length})
                    </button>
                </div>

                <div className="orders-section">
                    {loading && <p className="loading-text">Fetching latest data...</p>}
                    {error && <p className="error-text">{error}</p>}

                    {!loading && !error && activeTab === 'fulfillment' && (
                        <div className="table-wrapper">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>Order ID</th>
                                        <th>Fulfillment Date</th>
                                        <th>Assigned To</th>
                                        <th>Entity Name</th>
                                        <th>Warnings</th>
                                        <th>Alert Flag</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alerts.length > 0 ? (
                                        alerts.map(alertItem => (
                                            <React.Fragment key={alertItem.id}>
                                                <tr
                                                    onClick={() => toggleRow(alertItem.id)}
                                                    style={{ cursor: 'pointer' }}
                                                    className={expandedRow === alertItem.id ? 'expanded-row' : ''}
                                                >
                                                    <td>#{alertItem.order_id}</td>
                                                    <td>{alertItem.fulfillment_date}</td>
                                                    <td>
                                                        <span className={`assigned-badge ${alertItem.assigned_type}`}>
                                                            {alertItem.assigned_type?.charAt(0).toUpperCase() + alertItem.assigned_type?.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td>{alertItem.entity_name || '-'}</td>
                                                    <td>
                                                        <span className={`warning-badge ${alertItem.warning_count > 7 ? 'critical' : ''}`}>
                                                            {alertItem.warning_count || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`alert-badge ${alertItem.alert_flag > 0 ? 'critical' : ''}`}>
                                                            {alertItem.alert_flag || 0}
                                                        </span>
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {alertItem.is_blocked ? (
                                                            <button className="unblock-btn" onClick={() => handleUnblock(alertItem)}>Unblock</button>
                                                        ) : (
                                                            <button className="block-btn" onClick={() => handleBlockClick(alertItem)}>Block</button>
                                                        )}
                                                        <button className="ignore-btn" onClick={() => handleIgnore(alertItem)}>Ignore</button>
                                                    </td>
                                                </tr>
                                                {expandedRow === alertItem.id && (
                                                    <tr className="details-row">
                                                        <td colSpan="7">
                                                            <div className="order-detail-dropdown">
                                                                <h4>Fulfillment Details</h4>
                                                                <div className="detail-grid">
                                                                    <div><strong>Customer:</strong> {alertItem.customer_name}</div>
                                                                    <div><strong>Contact:</strong> {alertItem.contact_no}</div>
                                                                    <div><strong>Delivery Time:</strong> {formatTime(alertItem.delivery_time)}</div>
                                                                    <div><strong>Completed At:</strong> {formatDate(alertItem.completed_at)}</div>
                                                                    <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong> {alertItem.address_text}</div>
                                                                </div>
                                                                {alertItem.items && alertItem.items.length > 0 && (
                                                                    <div className="items-list">
                                                                        <strong>Items:</strong>
                                                                        <ul>
                                                                            {alertItem.items.map((item, idx) => (
                                                                                <li key={idx}>{item.name} x {item.quantity}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr><td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>No fulfillment alerts found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && !error && activeTab === 'assignment' && (
                        <div className="table-wrapper">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>Order ID</th>
                                        <th>Order Date</th>
                                        <th>Customer</th>
                                        <th>Location</th>
                                        <th>Requested Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unfulfilled.length > 0 ? (
                                        unfulfilled.map(order => (
                                            <React.Fragment key={order.order_id}>
                                                <tr
                                                    onClick={() => toggleUnfulfilledRow(order.order_id)}
                                                    style={{ cursor: 'pointer' }}
                                                    className={expandedUnfulfilled === order.order_id ? 'expanded-row' : ''}
                                                >
                                                    <td>#{order.order_id}</td>
                                                    <td>{order.start_date}</td>
                                                    <td>{order.customer_name}</td>
                                                    <td className="truncate-text">{order.address_text}</td>
                                                    <td>{formatTime(order.delivery_time)}</td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            className="ignore-btn"
                                                            onClick={() => handleIgnoreAssignment(order.order_id)}
                                                        >
                                                            Dismiss
                                                        </button>
                                                        <button
                                                            className="complete-btn"
                                                            style={{ marginLeft: '8px', backgroundColor: '#e67e22' }}
                                                            onClick={() => handleAssignClick(order)}
                                                        >
                                                            Assign
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedUnfulfilled === order.order_id && (
                                                    <tr className="details-row">
                                                        <td colSpan="6">
                                                            <div className="order-detail-dropdown">
                                                                <h4>Assignment Failure Details</h4>
                                                                <p className="failure-note">Reason: No suitable distributor or manufacturer found within service radius with requested stock and operating days.</p>
                                                                <div className="detail-grid">
                                                                    <div><strong>Contact:</strong> {order.contact_no}</div>
                                                                    <div><strong>Created At:</strong> {formatDate(order.created_at)}</div>
                                                                    <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong> {order.address_text}</div>
                                                                </div>
                                                                {order.items && order.items.length > 0 && (
                                                                    <div className="items-list">
                                                                        <strong>Unfulfilled Items:</strong>
                                                                        <ul>
                                                                            {order.items.map((item, idx) => (
                                                                                <li key={idx}>{item.name} x {item.quantity}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>No unfulfilled order assignments. Everything is under control!</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Block Confirmation Modal (Shared) */}
            {showBlockModal && (
                <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Block {blockTarget?.assigned_type}</h2>
                        <p>Block <strong>{blockTarget?.entity_name}</strong> from receiving new orders?</p>
                        <div className="block-duration-select">
                            <label>Block Duration:</label>
                            <select value={blockDuration} onChange={(e) => setBlockDuration(e.target.value)}>
                                <option value="1">1 Day</option>
                                <option value="3">3 Days</option>
                                <option value="7">7 Days</option>
                                <option value="always">Always (Until Unblocked)</option>
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowBlockModal(false)} disabled={blocking}>Cancel</button>
                            <button className="confirm-btn danger" onClick={confirmBlock} disabled={blocking}>{blocking ? 'Blocking...' : 'Confirm Block'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Assignment Modal */}
            {showAssignModal && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="modal-content assign-modal animated fadeIn" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Assign Order #{orderToAssign?.order_id}</h2>
                            <button className="close-btn" onClick={() => setShowAssignModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>Select a manufacturer or distributor to manually assign this order.</p>

                            <div className="selection-group">
                                <label>Target Entity Type:</label>
                                <div className="type-toggle">
                                    <button
                                        className={selectedEntity.type === 'manufacturer' ? 'active' : ''}
                                        onClick={() => setSelectedEntity({ ...selectedEntity, type: 'manufacturer', id: '' })}
                                    >
                                        Manufacturer
                                    </button>
                                    <button
                                        className={selectedEntity.type === 'distributor' ? 'active' : ''}
                                        onClick={() => setSelectedEntity({ ...selectedEntity, type: 'distributor', id: '' })}
                                    >
                                        Distributor
                                    </button>
                                </div>
                            </div>

                            {selectedEntity.type && (
                                <div className="selection-group animated slideDown">
                                    <label>Select {selectedEntity.type}:</label>
                                    <select
                                        value={selectedEntity.id}
                                        onChange={(e) => setSelectedEntity({ ...selectedEntity, id: e.target.value })}
                                    >
                                        <option value="">-- Choose {selectedEntity.type} --</option>
                                        {selectedEntity.type === 'manufacturer' ? (
                                            entities.manufacturers.map(m => (
                                                <option key={m.id} value={m.id}>{m.name}</option>
                                            ))
                                        ) : (
                                            entities.distributors.map(d => (
                                                <option key={d.id} value={d.id}>{d.name}</option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowAssignModal(false)} disabled={assigning}>Cancel</button>
                            <button
                                className="confirm-btn"
                                style={{ backgroundColor: '#27ae60' }}
                                onClick={confirmManualAssign}
                                disabled={assigning || !selectedEntity.id}
                            >
                                {assigning ? 'Assigning...' : 'Confirm Assignment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerAlerts;
